/**
 * Dynamic Tiered Offer Bar Web Component
 * Syncs cart progress between Cart Drawer, Main Cart, and Top Announcement Bar
 */
class HDTTieredOfferBar extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  connectedCallback() {
    this.bindEvents();
    this.updateStateFromCart();
  }

  init() {
    this.c1 = parseInt(this.dataset.c1 || '2');
    this.c2 = parseInt(this.dataset.c2 || '3');
    this.c3 = parseInt(this.dataset.c3 || '4');
    this.c4 = parseInt(this.dataset.c4 || '5');
    this.c5 = parseInt(this.dataset.c5 || '6');

    this.t1Label = this.dataset.t1Label || 'FREE';
    this.t2Label = this.dataset.t2Label || '-15%';
    this.t3Label = this.dataset.t3Label || '-20%';
    this.t4Label = this.dataset.t4Label || '-25%';
    this.t5Label = this.dataset.t5Label || '-30%';
    this.context = this.dataset.context || 'cart';
  }

  bindEvents() {
    // Modal info trigger
    const infoBtns = this.querySelectorAll('.js-info-trigger');
    const dialog = this.querySelector('.js-offer-dialog') || document.querySelector('.js-offer-dialog');

    infoBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (dialog) dialog.classList.add('is-open');
      });
    });

    if (dialog) {
      const closeBtn = dialog.querySelector('.js-dialog-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          dialog.classList.remove('is-open');
        });
      }
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.classList.remove('is-open');
      });
    }

    if (!window.hdtTieredDialogCloseBound) {
      window.hdtTieredDialogCloseBound = true;
      document.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('.js-dialog-close') || e.target.closest('.cart-tiered-offer-dialog__close');
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          document.querySelectorAll('.js-offer-dialog').forEach(d => d.classList.remove('is-open'));
        } else if (e.target.classList && e.target.classList.contains('js-offer-dialog')) {
          e.target.classList.remove('is-open');
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.keyCode === 27) {
          document.querySelectorAll('.js-offer-dialog').forEach(d => d.classList.remove('is-open'));
        }
      });
    }

    // Cart Change Listener
    const onCartUpdate = (event) => {
      let count = null;
      if (event && event.detail && typeof event.detail.item_count !== 'undefined') {
        count = event.detail.item_count;
      } else if (event && event.detail && event.detail.cart && typeof event.detail.cart.item_count !== 'undefined') {
        count = event.detail.cart.item_count;
      }

      if (count !== null) {
        this.renderItemCount(count);
      } else {
        this.fetchCart();
      }
    };

    document.addEventListener('cart:drawer:change', onCartUpdate);
    document.addEventListener('cart:updated', onCartUpdate);
    document.addEventListener('cart:refresh', onCartUpdate);

    // Global fetch interceptor fallback for AJAX cart actions
    if (!window.hdtTieredCartFetchIntercepted) {
      window.hdtTieredCartFetchIntercepted = true;
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url);
        if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update'))) {
          try {
            const clone = response.clone();
            const cartData = await clone.json();
            const itemCount = typeof cartData.item_count !== 'undefined' ? cartData.item_count : (cartData.cart ? cartData.cart.item_count : null);
            if (itemCount !== null) {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: itemCount } }));
            }
          } catch (err) {}
        }
        return response;
      };
    }
  }

  async fetchCart() {
    try {
      const res = await fetch(`${window.Shopify ? window.Shopify.routes.root : '/'}cart.js`);
      const cart = await res.json();
      this.renderItemCount(cart.item_count);
    } catch (e) {
      console.warn('Failed to fetch cart for tiered offer:', e);
    }
  }

  updateStateFromCart() {
    const currentCount = parseInt(this.dataset.itemCount || '0');
    this.renderItemCount(currentCount);
  }

  renderItemCount(count) {
    this.dataset.itemCount = count;

    // Calculate Progress % across 5 nodes (4 step intervals of 20%)
    let percent = 0;
    if (count <= 0) {
      percent = 0;
    } else if (count < this.c1) {
      percent = (count / this.c1) * 20;
    } else if (count >= this.c1 && count < this.c2) {
      const diff = Math.max(1, this.c2 - this.c1);
      percent = 20 + ((count - this.c1) / diff) * 20;
    } else if (count >= this.c2 && count < this.c3) {
      const diff = Math.max(1, this.c3 - this.c2);
      percent = 40 + ((count - this.c2) / diff) * 20;
    } else if (count >= this.c3 && count < this.c4) {
      const diff = Math.max(1, this.c4 - this.c3);
      percent = 60 + ((count - this.c3) / diff) * 20;
    } else if (count >= this.c4 && count < this.c5) {
      const diff = Math.max(1, this.c5 - this.c4);
      percent = 80 + ((count - this.c4) / diff) * 20;
    } else {
      percent = 100;
    }

    // Track Fill
    const trackFill = this.querySelector('.js-track-fill');
    if (trackFill) {
      trackFill.style.width = `${percent}%`;
    }

    // Nodes
    const n1 = this.querySelector('.js-node-1');
    const n2 = this.querySelector('.js-node-2');
    const n3 = this.querySelector('.js-node-3');
    const n4 = this.querySelector('.js-node-4');
    const n5 = this.querySelector('.js-node-5');

    if (n1) n1.classList.toggle('is-reached', count >= this.c1);
    if (n2) n2.classList.toggle('is-reached', count >= this.c2);
    if (n3) n3.classList.toggle('is-reached', count >= this.c3);
    if (n4) n4.classList.toggle('is-reached', count >= this.c4);
    if (n5) n5.classList.toggle('is-reached', count >= this.c5);

    // Status Message
    const statusText = this.querySelector('.js-status-text');
    if (statusText) {
      let msg = '';
      if (count < this.c1) {
        const rem = this.c1 - count;
        msg = `Add ${rem} more item${rem > 1 ? 's' : ''} to get <strong>FREE shipping</strong>`;
      } else if (count < this.c2) {
        const rem = this.c2 - count;
        msg = `Only ${rem} left to get <strong>${this.t2Label} OFF</strong>`;
      } else if (count < this.c3) {
        const rem = this.c3 - count;
        msg = `Only ${rem} left to get <strong>${this.t3Label} OFF</strong>`;
      } else if (count < this.c4) {
        const rem = this.c4 - count;
        msg = `Only ${rem} left to get <strong>${this.t4Label} OFF</strong>`;
      } else if (count < this.c5) {
        const rem = this.c5 - count;
        msg = `Only ${rem} left to get <strong>${this.t5Label} OFF</strong>`;
      } else {
        msg = `You unlocked maximum <strong>${this.t5Label} OFF</strong>!`;
      }
      statusText.innerHTML = msg;
    }
  }
}

if (!customElements.get('hdt-tiered-offer-bar')) {
  customElements.define('hdt-tiered-offer-bar', HDTTieredOfferBar);
}
